import { useCallback, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useLocation, useParams } from "react-router";
import { stepDisplayStatesOf, type ThreadOverview } from "../../../api/threads/threadDecoders";
import ErrorNotice from "../../../errors/ErrorNotice";
import { useOpenedProject } from "../../../projects/useOpenedProject";
import { appPaths } from "../../../shared/routing/appPaths";
import { parseCollectionVersionRef } from "../../../shared/references/contentReferences";
import { useTabTitle } from "../../../shell/workspace";
import LoadStatus from "../../../ui/feedback/LoadStatus";
import type { LoadedProcess } from "../../workflows/data/processCatalogue";
import { RunStepDetails } from "../../workflows";
import ProcessView from "../../workflows/view/ProcessView";
import { useProcessViewChoices } from "../../workflows/view/useProcessViewChoices";
import { useMemberNames } from "../data/useMemberNames";
import { useThreadOverview } from "../data/useThreadOverview";
import { useThreadProcess } from "../data/useThreadProcess";
import { useLiveThread } from "../live/threadLiveContext";
import { useStepPlans } from "../page/currentStep/useStepPlans";
import { requestWaitText } from "../status/requestWaitWords";
import RunGraphHeader from "./RunGraphHeader";
import RunStepParts from "./RunStepParts";

/**
 * `/threads/<id>/graph`: a Run as its Workflow's graph, live (WD-4; Johannes, 30 Oct 11:35),
 * opened beside its Run view. Module C says every step's state and what its latest attempt
 * made (W-22); the graph only shows it. It follows the Workflow the Run follows now, so a
 * switch shows the new Workflow's graph, never the old one with the new states (X09).
 */
export default function RunGraphPage() {
  const { threadId } = useParams();
  const location = useLocation();
  if (threadId === undefined) {
    return <ErrorNotice error={{ kind: "invalidAddress", address: location.pathname }} className="p-4" />;
  }
  return <RunGraph key={threadId} threadId={threadId} />;
}

function RunGraph({ threadId }: { threadId: string }) {
  const { t } = useTranslation();
  const [startAfter, setStartAfter] = useState<number | null>(null);
  const live = useLiveThread(threadId, startAfter);
  const { state: overview, reload } = useThreadOverview(threadId, live);
  // Follow from the overview's last entry: the graph shows the state, not the earlier entries.
  if (startAfter === null && overview.status === "loaded") {
    setStartAfter(overview.value.lastEntryNumber);
  }
  if (overview.status !== "loaded") {
    return <LoadStatus state={overview} loadingLabel={t("threads.page.loading")} loadingClassName="py-10" onRetry={reload} failedClassName="p-4" />;
  }
  return <LoadedRunGraph overview={overview.value} />;
}

function LoadedRunGraph({ overview }: { overview: ThreadOverview }) {
  const { t } = useTranslation();
  useOpenedProject(overview.projectId);
  useTabTitle({ kind: "thread", name: t("threads.runGraph.tabTitle", { title: overview.title }) });
  // The run reads its project items at the Run's bound content version (F103).
  const commit = parseCollectionVersionRef(overview.bindings.content_version)?.commit ?? null;
  const { state: process, reload } = useThreadProcess(overview.workflow, overview.projectId, commit);
  return (
    <div className="space-y-4 py-4">
      <RunGraphHeader overview={overview} workflowName={process.status === "loaded" ? (process.value?.workflow.name ?? null) : null} />
      {overview.workflow === null ? (
        <p className="app-text-muted text-sm">{t("threads.runGraph.noWorkflow")}</p>
      ) : (
        <>
          <LoadStatus state={process} loadingLabel={t("workflows.view.loading")} loadingClassName="py-10" onRetry={reload} />
          {/* The states are of the Workflow C names now: shown only on that Workflow's graph (X09). */}
          {process.status === "loaded" && process.value !== null && process.value.workflow.reference === overview.workflow ? (
            <RunProcessView process={process.value} overview={overview} />
          ) : null}
        </>
      )}
    </div>
  );
}

/**
 * The graph of the Workflow the Run follows. Its cards' axes come from the Run's own step plans, as the Run view's
 * steps beside take them: module B plans any Workflow a Run follows, a draft's too, and with what has run (WD-1+3).
 * A waiting step's card says what it waits for in the Run view's words (U-3).
 */
function RunProcessView({ process, overview }: { process: LoadedProcess; overview: ThreadOverview }) {
  const { t } = useTranslation();
  const choices = useProcessViewChoices();
  const { threadId } = overview;
  const plans = useStepPlans(threadId, overview.lastEntryNumber, overview.workflow !== null);
  const people = useMemberNames(overview.projectId);
  const waiting = overview.projection.open_human_step ?? null;
  const hostName = people.currentUserId === overview.host ? null : people.nameOf(overview.host);
  const run = useMemo(
    () => ({
      detail: overview.detail,
      waits: {
        slot: t("threads.turnActivity.waitingForSlot", { pc: t("threads.toolApproval.hostPc") }),
        approval: t("threads.turnActivity.awaitingApproval"),
        offline: t("threads.turnActivity.hostOffline"),
        input: waiting === null ? null : requestWaitText(waiting, null, hostName, t),
      },
    }),
    [overview.detail, waiting, hostName, t],
  );
  // A step's state opens the Run view beside, where its entries are (WD-4).
  const stepLink = useCallback(() => appPaths.thread(threadId), [threadId]);
  // An open agent card shows what its prompt is filled with before it runs (F148).
  const shown = useMemo(() => stepDisplayStatesOf(overview.detail), [overview.detail]);
  const stepParts = useCallback(
    (progressKey: string) => (
      <RunStepParts threadId={threadId} projectId={overview.projectId} plan={plans.get(progressKey)} beforeItRuns={!RAN.has(shown[progressKey] ?? "upcoming")} lastEntryNumber={overview.lastEntryNumber} />
    ),
    [threadId, overview.projectId, overview.lastEntryNumber, plans, shown],
  );
  return (
    <RunStepDetails.Provider value={stepParts}>
    <ProcessView
      process={process}
      plans={plans}
      display={choices.display}
      onDisplayChange={choices.setDisplay}
      density={choices.density}
      onDensityChange={choices.setDensity}
      layout={choices.layout}
      onLayoutChange={choices.setLayout}
      run={run}
      stepLinkInThread={stepLink}
      canvas="page"
    />
    </RunStepDetails.Provider>
  );
}

/** The states a step has run in, or runs in now: its card shows what it sent through its state, not a fill. */
const RAN: ReadonlySet<string> = new Set(["running", "done", "skipped", "queued", "approval", "slot", "offline"]);
